import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { endpoints } from '../api/endpoints';
import { useAuth } from '../auth/AuthContext';
import { downloadBlob, ErrorBox, Loading, useAsync } from '../components/common';
import { ActivityCharts, SessionsTable } from './DashboardPage';

function MetricsTable({ metrics }: { metrics: Record<string, unknown> }) {
  const rows = Object.entries(metrics).filter(([, v]) => v === null || ['number', 'string', 'boolean'].includes(typeof v));
  return (
    <table>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <th scope="row">{k}</th>
            <td>{typeof v === 'number' ? Number(v.toFixed(2)) : String(v ?? '—')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function StatisticsPage() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [scope, setScope] = useState<'me' | 'organization'>('me');
  const [range, setRange] = useState({ from: '', to: '', scope: 'me' as 'me' | 'organization' });
  const [error, setError] = useState<unknown>(null);

  const metrics = useAsync(() => endpoints.analytics.metrics(range), [range]);
  const progress = useAsync(() => endpoints.analytics.userProgress({ days: 30 }), []);
  const sessions = useAsync(() => endpoints.analytics.sessions({ from: range.from, to: range.to }), [range]);

  const exportData = async (format: 'csv' | 'json') => {
    try {
      const response = await endpoints.analytics.export(format, { from: range.from, to: range.to });
      downloadBlob(await response.blob(), `speakmanager-analytics.${format}`);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div>
      <h1>{t('stats.title')}</h1>
      <form
        className="filters"
        onSubmit={(e) => {
          e.preventDefault();
          setRange({ from, to, scope });
        }}
      >
        <label>
          {t('stats.from')}
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          {t('stats.to')}
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {hasRole('admin') && (
          <label>
            {t('stats.scope')}
            <select value={scope} onChange={(e) => setScope(e.target.value as 'me' | 'organization')}>
              <option value="me">{t('stats.scopeMe')}</option>
              <option value="organization">{t('stats.scopeOrg')}</option>
            </select>
          </label>
        )}
        <button type="submit">{t('stats.apply')}</button>
        <button type="button" className="secondary" onClick={() => void exportData('csv')}>
          {t('stats.exportCsv')}
        </button>
        <button type="button" className="secondary" onClick={() => void exportData('json')}>
          {t('stats.exportJson')}
        </button>
      </form>
      <ErrorBox error={metrics.error || error} onRetry={metrics.reload} />
      <section className="card">
        <h2>{t('stats.metrics')}</h2>
        {metrics.loading ? <Loading /> : <MetricsTable metrics={(metrics.data ?? {}) as Record<string, unknown>} />}
      </section>
      <ActivityCharts points={progress.data?.points ?? []} />
      <section>
        <h2>{t('sessions.title')}</h2>
        <SessionsTable sessions={sessions.data ?? []} />
      </section>
    </div>
  );
}
